import React, { useState, useMemo, useEffect } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "../../appointmentsPage.module.css";
import {
  IAppointment,
  IAppointmentStats,
} from "../../types/appointments.types";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import AppointmentCard from "../AppointmentCard/AppointmentCard";
import AppointmentDetailsModal from "../AppointmentDetailsModal/AppointmentDetailsModal";
import CreateAppointmentModal from "../CreateAppointmentModal/CreateAppointmentModal";
import {
  getInitialDateForView,
  saveDateForView,
} from "../../utils/calendarStorage";
import ExportTableButtons from "../../../../components/exportTableButtons/ExportTableButtons";
import {
  buildAppointmentsExport,
  buildSearchFilterSummary,
} from "../../utils/buildAppointmentsExport";

// Get locale based on current language
const getLocale = () => (i18n.language === "he" ? "he-IL" : "en-US");

/**
 * Daily View Component - Client Version (Read-Only)
 *
 * Displays appointments for a single day
 * Read-only version without create/edit functionality
 */
const DailyView: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(() =>
    getInitialDateForView("daily"),
  );
  const [tempSelectedDate, setTempSelectedDate] = useState<string | null>(null);
  const [selectedAppointment, setSelectedAppointment] =
    useState<IAppointment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalDate, setCreateModalDate] = useState("");
  const [createModalTime, setCreateModalTime] = useState("09:00");

  // Use shared appointments context
  const { searchedAppointments, searchTerm, canSelfBook } = useAppointmentsContext();

  // Persist date when it changes (for navigation back)
  useEffect(() => {
    saveDateForView("daily", selectedDate);
  }, [selectedDate]);

  // Filter searched appointments for selected day
  const { dayStart, dayEnd } = useMemo(() => {
    const start = new Date(selectedDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(selectedDate);
    end.setHours(23, 59, 59, 999);
    return { dayStart: start, dayEnd: end };
  }, [selectedDate]);

  const filteredAppointments = useMemo(() => {
    // Filter searchedAppointments (already filtered by search term) by date
    return searchedAppointments.filter((apt) => {
      const aptDate = new Date(apt.date);
      return aptDate >= dayStart && aptDate <= dayEnd;
    });
  }, [searchedAppointments, dayStart, dayEnd]);

  // Sort by time
  const sortedAppointments = useMemo(() => {
    return [...filteredAppointments].sort((a, b) =>
      a.time.localeCompare(b.time),
    );
  }, [filteredAppointments]);

  // Calculate stats
  const stats: IAppointmentStats = useMemo(() => {
    const total = filteredAppointments.length;

    return {
      totalToday: total,
      confirmed: total,
      pending: 0,
      cancelled: 0,
    };
  }, [filteredAppointments]);

  // Export payload (PDF/Excel) for the currently displayed, filtered day.
  const exportProps = useMemo(
    () =>
      buildAppointmentsExport(filteredAppointments, {
        viewType: "daily",
        rangeLabel: selectedDate.toLocaleDateString(getLocale(), {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        filterSummary: buildSearchFilterSummary(searchTerm),
      }),
    [filteredAppointments, selectedDate, searchTerm],
  );

  const handleAppointmentClick = (appointment: IAppointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleDateChange = (direction: "prev" | "next") => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
    setSelectedDate(newDate);
  };

  const formatDate = (date: Date): string => {
    const days = [
      t("APPOINTMENTS_DAY_SUNDAY"),
      t("APPOINTMENTS_DAY_MONDAY"),
      t("APPOINTMENTS_DAY_TUESDAY"),
      t("APPOINTMENTS_DAY_WEDNESDAY"),
      t("APPOINTMENTS_DAY_THURSDAY"),
      t("APPOINTMENTS_DAY_FRIDAY"),
      t("APPOINTMENTS_DAY_SATURDAY"),
    ];
    const dayName = days[date.getDay()];
    return `${t("APPOINTMENTS_DAY_DATE")} ${dayName}, ${date.getDate()}/${
      date.getMonth() + 1
    }/${date.getFullYear()}`;
  };

  return (
    <div>
      {/* Statistics */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{stats.totalToday}</div>
          <div className={styles.statLabel}>{t("APPOINTMENTS_TITLE")}</div>
        </div>
      </div>

      {/* Calendar Section */}
      <div className={styles.calendarSection}>
        <div className={styles.calendarHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <h2 className={styles.calendarTitle}>{formatDate(selectedDate)}</h2>
            {canSelfBook && (
              <button
                className={styles.addAppointmentBtn}
                onClick={() => {
                  setCreateModalDate(selectedDate.toISOString().split("T")[0]);
                  setCreateModalTime("09:00");
                  setShowCreateModal(true);
                }}
                title={t("APPOINTMENTS_NEW_TITLE")}
              >
                +
              </button>
            )}
          </div>
          <div className={styles.calendarNav}>
            <button
              className={styles.navBtn}
              onClick={() => handleDateChange("next")}
              aria-label="Next"
            >
              ‹
            </button>
            <input
              type="date"
              className={styles.datePicker}
              value={
                tempSelectedDate || selectedDate.toISOString().split("T")[0]
              }
              onChange={(e) => {
                setTempSelectedDate(e.target.value);
              }}
            />
            {tempSelectedDate && (
              <button
                className={styles.selectDateBtn}
                onClick={() => {
                  const newDate = new Date(tempSelectedDate);
                  setSelectedDate(newDate);
                  setTempSelectedDate(null);
                }}
              >
                {t("SELECT")}
              </button>
            )}
            <button
              className={styles.todayBtn}
              onClick={() => {
                setSelectedDate(new Date());
                setTempSelectedDate(null);
              }}
            >
              {t("APPOINTMENTS_TODAY")}
            </button>
            <span className={styles.currentDate}>
              {selectedDate.toLocaleDateString(getLocale(), {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              className={styles.navBtn}
              onClick={() => handleDateChange("prev")}
              aria-label="Previous"
            >
              ›
            </button>
            <ExportTableButtons
              disabled={filteredAppointments.length === 0}
              {...exportProps}
            />
          </div>
        </div>

        {/* Appointments List */}
        {sortedAppointments.length > 0 ? (
          <div className={styles.appointmentsGrid}>
            {sortedAppointments.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                onClick={() => handleAppointmentClick(appointment)}
              />
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📅</div>
            <p className={styles.emptyText}>
              {t("APPOINTMENTS_NO_APPOINTMENTS")}
            </p>
          </div>
        )}
      </div>

      {/* Appointment Details Modal */}
      {isModalOpen && selectedAppointment && (
        <AppointmentDetailsModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
        />
      )}

      {/* Create Appointment Modal */}
      {showCreateModal && (
        <CreateAppointmentModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => setShowCreateModal(false)}
          initialDate={createModalDate}
          initialTime={createModalTime}
        />
      )}
    </div>
  );
};

export default DailyView;
